import { useEffect, useRef, useState } from "react";
import { useDeleteSubmission, useSubmissionDetail } from "./useSubmissions.js";
import { useAuth } from "../auth/AuthContext.js";
import { SensitiveAnswer } from "./SensitiveAnswer.js";

/**
 * The row-expansion detail drawer — docs/03-API-SPEC.md §6: rendered against the submission's own
 * form version, with consent records and the audit trail.
 *
 * The version matters and is shown: a submission from version 3 answered different questions from
 * one made after version 4 published, and a reviewer comparing two applications needs to know which
 * they are looking at.
 */
export const DetailDrawer = ({
  submissionId,
  onClose,
}: {
  submissionId: string | null;
  onClose: () => void;
}): JSX.Element | null => {
  const query = useSubmissionDetail(submissionId);
  const { user } = useAuth();
  const remove = useDeleteSubmission();
  const [confirming, setConfirming] = useState(false);
  const closeRef = useRef<HTMLButtonElement>(null);

  // Reopening the drawer on another submission must not inherit a half-armed confirmation from
  // the last one — that is how the wrong application gets deleted. Adjusted during render rather
  // than in an effect: an effect would leave one paint showing the previous submission's armed
  // state, and would have to depend on the mutation object, which changes every render.
  const [confirmingFor, setConfirmingFor] = useState(submissionId);
  if (confirmingFor !== submissionId) {
    setConfirmingFor(submissionId);
    setConfirming(false);
  }

  useEffect(() => {
    if (submissionId) closeRef.current?.focus();
  }, [submissionId]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!submissionId) return null;

  const detail = query.data;
  // Mirrors the server's `submission.delete`, which remains the authority — this only decides
  // whether offering the control would be an empty promise.
  const canDelete =
    detail !== undefined &&
    (user?.isSuperadmin === true ||
      (user?.programmeRoles ?? []).some(
        (entry) =>
          entry.programmeId === detail.form.programmeId && entry.role === "PROGRAMME_ADMIN",
      ));

  return (
    <aside
      role="dialog"
      aria-modal="true"
      aria-label="Submission detail"
      className="fixed right-0 top-0 z-30 flex h-full w-full max-w-xl flex-col overflow-auto border-l bg-background shadow-xl"
    >
      <header className="flex items-center justify-between border-b px-4 py-3">
        <div>
          <p className="font-medium">{detail?.reference ?? "Submission"}</p>
          <p className="text-sm text-muted-foreground">
            {detail?.form.title}
            {detail?.formVersion ? ` · version ${detail.formVersion.version}` : ""}
            {detail?.cycle ? ` · ${detail.cycle}` : ""}
          </p>
        </div>
        <button
          ref={closeRef}
          type="button"
          onClick={onClose}
          className="rounded-md border px-3 py-1.5 text-sm"
        >
          Close
        </button>
      </header>

      {query.isLoading && <p className="p-4 text-sm text-muted-foreground">Loading…</p>}
      {query.isError && (
        <p role="alert" className="p-4 text-sm text-brand-red">
          Could not load this submission.
        </p>
      )}

      {detail && (
        <div className="flex flex-col gap-6 p-4 text-sm">
          {detail.piiMasked && (
            <p className="rounded-md bg-muted px-3 py-2 text-muted-foreground">
              Personal data is masked for your role.
            </p>
          )}

          <section>
            <h3 className="mb-2 font-medium">Applicant</h3>
            <dl className="grid grid-cols-[10rem_1fr] gap-y-1">
              <dt className="text-muted-foreground">Name</dt>
              <dd>{detail.respondent?.displayName ?? "—"}</dd>
              <dt className="text-muted-foreground">Email</dt>
              <dd>{detail.respondent?.email ?? "—"}</dd>
              <dt className="text-muted-foreground">Review status</dt>
              <dd>{detail.reviewStatus}</dd>
              <dt className="text-muted-foreground">Submitted</dt>
              <dd>
                {detail.submittedAt
                  ? new Date(detail.submittedAt).toLocaleString("en-NG", {
                      timeZone: "Africa/Lagos",
                    })
                  : "—"}
              </dd>
            </dl>
          </section>

          <section>
            <h3 className="mb-2 font-medium">Answers</h3>
            <dl className="grid grid-cols-[12rem_1fr] gap-y-1">
              {detail.fields.map((field) => (
                <div key={field.key} className="contents">
                  <dt className="text-muted-foreground">{field.label}</dt>
                  <dd className="break-words">
                    {field.isSensitive ? (
                      <SensitiveAnswer
                        submissionId={detail.id}
                        fieldKey={field.key}
                        canReveal={field.canReveal}
                      />
                    ) : (
                      <>
                        {formatValue(detail.data[field.key])}
                        {field.isPii && detail.piiMasked && (
                          <span className="ml-1 text-xs text-muted-foreground">(masked)</span>
                        )}
                      </>
                    )}
                  </dd>
                </div>
              ))}
            </dl>
          </section>

          {detail.consents.length > 0 && (
            <section>
              <h3 className="mb-2 font-medium">Consent</h3>
              <ul className="list-disc pl-5">
                {detail.consents.map((consent) => (
                  <li key={`${consent.policyKey}-${consent.policyVersion}`}>
                    {consent.policyKey} v{consent.policyVersion} —{" "}
                    {new Date(consent.acceptedAt).toLocaleDateString("en-NG", {
                      timeZone: "Africa/Lagos",
                    })}
                  </li>
                ))}
              </ul>
            </section>
          )}

          <section>
            <h3 className="mb-2 font-medium">Audit trail</h3>
            {detail.auditTrail.length === 0 ? (
              <p className="text-muted-foreground">Nothing recorded against this submission yet.</p>
            ) : (
              <ul className="flex flex-col gap-1">
                {detail.auditTrail.map((entry, index) => (
                  <li key={index} className="flex justify-between gap-2">
                    <span>{entry.action}</span>
                    <span className="text-muted-foreground">
                      {entry.actorLabel ?? "system"} ·{" "}
                      {new Date(entry.createdAt).toLocaleString("en-NG", {
                        timeZone: "Africa/Lagos",
                      })}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {canDelete && (
            <section className="border-t pt-4">
              {!confirming ? (
                <button
                  type="button"
                  onClick={() => setConfirming(true)}
                  className="rounded-md border border-brand-red px-3 py-1.5 text-sm text-brand-red"
                >
                  Delete this submission
                </button>
              ) : (
                <div className="rounded-md border border-brand-red p-3">
                  <p className="text-sm font-medium">
                    Delete {detail?.reference ?? "this submission"}?
                  </p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    It stops appearing in the table and exports, and the form&rsquo;s submission
                    count goes down by one. The record and its audit trail are kept, and the
                    reference number is not reissued. To erase an applicant&rsquo;s data at their
                    request, use a data request instead.
                  </p>
                  {remove.isError && (
                    <p role="alert" className="mt-2 text-sm text-brand-red">
                      That did not work. Nothing has been deleted.
                    </p>
                  )}
                  <div className="mt-3 flex gap-2">
                    <button
                      type="button"
                      disabled={remove.isPending}
                      onClick={() => {
                        if (!submissionId) return;
                        remove.mutate(submissionId, { onSuccess: onClose });
                      }}
                      className="rounded-md bg-brand-red px-3 py-1.5 text-sm text-white disabled:opacity-60"
                    >
                      {remove.isPending ? "Deleting\u2026" : "Yes, delete it"}
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirming(false)}
                      className="rounded-md border px-3 py-1.5 text-sm"
                    >
                      Keep it
                    </button>
                  </div>
                </div>
              )}
            </section>
          )}
        </div>
      )}
    </aside>
  );
};

const formatValue = (value: unknown): string => {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "object") {
    return Array.isArray(value)
      ? value.map((entry) => formatValue(entry)).join(", ")
      : Object.values(value as Record<string, unknown>)
          .map((entry) => formatValue(entry))
          .filter((entry) => entry !== "—")
          .join(" ");
  }
  return String(value);
};
